// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import api from './api'
import { saveBlob } from '../../lib/download'

export interface CreateInvoiceData {
  invoice_number: string
  issue_date: string
  due_date: string
  total_amount: number
  payment_status: 'paid' | 'unpaid' | 'partial'
}

export interface InvoiceResponse {
  success: boolean
  data?: any
  message?: string
  errors?: string[]
}

export const invoicesApi = {
  // Create invoice for a specific deal
  createForDeal: async (dealId: string, customerId: string, invoiceData: CreateInvoiceData, file?: File | null): Promise<InvoiceResponse> => {
    try {
      let requestData: any
      let headers: any = {}

      if (file) {
        // Use FormData for file upload
        const formData = new FormData()
        formData.append('invoice[invoice_number]', invoiceData.invoice_number)
        formData.append('invoice[issue_date]', invoiceData.issue_date)
        formData.append('invoice[due_date]', invoiceData.due_date)
        formData.append('invoice[total_amount]', invoiceData.total_amount.toString())
        formData.append('invoice[payment_status]', invoiceData.payment_status)
        formData.append('invoice[customer_deal_id]', dealId)
        formData.append('invoice[customer_id]', customerId)
        formData.append('file', file)

        requestData = formData
        headers['Content-Type'] = 'multipart/form-data'
      } else {
        // Use JSON for no file
        requestData = {
          invoice: {
            ...invoiceData,
            customer_deal_id: dealId,
            customer_id: customerId
          }
        }
      }

      const response = await api.post('/invoices', requestData, {
        headers
      })
      
      return {
        success: true,
        data: response.data,
        message: 'Invoice created successfully'
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to create invoice']
      }
    }
  },

  // Mark invoice as paid
  markAsPaid: async (invoiceId: string, paidDate?: string): Promise<InvoiceResponse> => {
    try {
      const response = await api.post(`/invoices/${invoiceId}/mark_as_paid`, {
        paid_date: paidDate || new Date().toISOString().split('T')[0] // Use provided date or current date
      })
      return {
        success: true,
        data: response.data,
        message: 'Invoice marked as paid'
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to mark invoice as paid']
      }
    }
  },

  // Mark invoice as unpaid
  markAsUnpaid: async (invoiceId: string): Promise<InvoiceResponse> => {
    try {
      const response = await api.post(`/invoices/${invoiceId}/mark_as_unpaid`)
      return {
        success: true,
        data: response.data,
        message: 'Invoice marked as unpaid'
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to mark invoice as unpaid']
      }
    }
  },

  // Get invoice details
  getById: async (invoiceId: string): Promise<InvoiceResponse> => {
    try {
      const response = await api.get(`/invoices/${invoiceId}`)
      return {
        success: true,
        data: response.data,
        message: 'Invoice retrieved successfully'
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to get invoice']
      }
    }
  },

  // Update invoice
  update: async (invoiceId: string, invoiceData: any, file?: File): Promise<InvoiceResponse> => {
    try {
      let requestData: FormData | any
      
      if (file) {
        // If file is provided, use FormData
        requestData = new FormData()
        Object.keys(invoiceData).forEach(key => {
          if (invoiceData[key] !== null && invoiceData[key] !== undefined) {
            requestData.append(key, invoiceData[key].toString())
          }
        })
        requestData.append('invoice_file', file)
      } else {
        // If no file, send as JSON wrapped in invoice key
        requestData = { invoice: invoiceData }
      }

      const response = await api.put(`/invoices/${invoiceId}`, requestData, {
        headers: file ? {} : { 'Content-Type': 'application/json' }
      })
      
      return {
        success: true,
        data: response.data,
        message: 'Invoice updated successfully'
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to update invoice']
      }
    }
  },

  // Delete invoice
  delete: async (invoiceId: string): Promise<InvoiceResponse> => {
    try {
      const response = await api.delete(`/invoices/${invoiceId}`)
      return {
        success: true,
        data: response.data,
        message: 'Invoice deleted successfully'
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to delete invoice']
      }
    }
  },

  // Get invoice by ID (returns data directly)
  getInvoiceById: async (invoiceId: string): Promise<any> => {
    const response = await invoicesApi.getById(invoiceId);
    if (!response.success) {
      throw new Error(response.errors?.[0] || 'Failed to get invoice');
    }
    return response.data;
  },

  // Download invoice file via secure custom endpoint
  downloadFile: async (invoiceId: string, originalFilename?: string): Promise<{ success: boolean; error?: string }> => {
    try {
      
      // Just download the file - the backend IS sending the correct filename in Content-Disposition header
      const response = await api.get(`/invoices/${invoiceId}/download`, {
        responseType: 'blob', // Important for binary file data
        timeout: 30000 // 30 second timeout for large files
      })
      
      
      // Get content type from response headers (case-insensitive)
      const responseContentType = response.headers['content-type'] || response.headers['Content-Type'] || 'application/octet-stream'
      
      // Create blob with PROPER content type - this is crucial!
      const blob = new Blob([response.data], { type: responseContentType })

      
      // Extract filename from Content-Disposition header - THE BACKEND IS SENDING THIS CORRECTLY!
      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition']
      
      
      let filename = `invoice-${invoiceId}.pdf` // fallback
      
      // First priority: use the original filename if provided
      if (originalFilename && originalFilename.trim()) {
        filename = originalFilename.trim()
      } else if (contentDisposition) {
        // Simple regex to extract filename
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, '').trim()
        } else {
          console.warn('❌ Failed to parse filename from Content-Disposition:', contentDisposition)
        }
      } else {
        console.warn('❌ NO Content-Disposition header found and no original filename provided!')
        console.warn('Available headers:', Object.keys(response.headers))
      }
      
      
      // Native OS Save-As dialog in the desktop app; browser download on the web.
      await saveBlob(filename, blob)

      return { success: true }
      
    } catch (error: any) {
      console.error('❌ Secure download error:', error)
      
      if (error.response?.status === 403) {
        return { 
          success: false, 
          error: 'Access denied - insufficient permissions' 
        }
      } else if (error.response?.status === 404) {
        return { 
          success: false, 
          error: 'No file attached to this invoice' 
        }
      } else if (error.response?.status === 401) {
        return { 
          success: false, 
          error: 'Authentication required' 
        }
      }
      
      return { 
        success: false, 
        error: error.message || 'Failed to download invoice file' 
      }
    }
  }
}
